import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { MicrosoftGraphKeysSettingStore } from './microsoft-graph-keys-setting.store';
import { routeParams, translate } from '../../../../modules/shared/shared.global-service';
import { FilterInput } from '@it2go/data-grid';
import { TableColumn, TableColumnType } from '../../../../modules/shared/components/kit/table/table.types';
import {
    MicrosoftGraphKeysSettingObject,
} from '@libs/types/src/inbox/object-type/microsoft-graph-keys-setting.object';
import { TableComponent } from '../../../../modules/shared/components/kit/table/table.component';
import { BaseDialogService } from '../../../kit/base-dialog/base-dialog-service';
import {
    MicrosoftGraphKeysSettingDialogComponent,
} from './microsoft-graph-keys-setting-dialog/microsoft-graph-keys-setting-dialog.component';
import { ConfirmBuilder } from '../../../../modules/shared/service/confirm.builder';
import { successDeleted } from '../../../../modules/content/content.error';
import { SwitchInputComponent } from '../../../kit/input/switch-input-v2/switch-input.component';
import { debounce, form, FormField } from '@angular/forms/signals';
import config from '../../../../config/config';

@Component({
    selector: 'app-microsoft-graph-keys-setting',
    templateUrl: './microsoft-graph-keys-setting.component.html',
    styleUrls: ['./microsoft-graph-keys-setting.component.scss'],
    providers: [MicrosoftGraphKeysSettingStore],
    imports: [
        FormField,
        TableComponent,
        SwitchInputComponent,
    ],
})
export class MicrosoftGraphKeysSettingComponent {
    protected readonly store = inject(MicrosoftGraphKeysSettingStore);
    private readonly dialogService = inject(BaseDialogService);
    private readonly confirm = inject(ConfirmBuilder);

    protected readonly formData = signal<{ showOnlyCurrent: boolean }>({
        showOnlyCurrent: true,
    });
    protected readonly formDef = form(this.formData, schema => {
        debounce(schema.showOnlyCurrent, config.debounceTime);
    });
    protected showOnlyCurrentDebounce = this.formDef.showOnlyCurrent().value;

    protected readonly prefix = 'organization-settings.emailClientSettings.';
    protected readonly columns: TableColumn[] = [
        {
            name: 'domain',
            filterable: true,
        },
        {
            name: 'tenantId',
            filterable: true,
        },
        {
            name: 'dateTo',
            type: TableColumnType.Date,
            filterable: { type: 'date' },
            sortable: true,
        },
        {
            name: 'sendReminderToEmail',
            filterable: true,
        },
    ];
    protected filters = computed((): FilterInput[][] => {
         const showOnlyCurrentDebounce = this.showOnlyCurrentDebounce();
         const organizationId = this.organizationId();
         if (!organizationId) {
             return [];
         }

         return [
             [{ column: 'organizationId', operator: 'EQ', value: [organizationId] }],
             ...(
                 showOnlyCurrentDebounce
                     ? [[{ column: 'isCurrent', operator: 'EQ', value: ['1'] }]]
                     : []
             ),
         ];
    });

    private organizationId = routeParams.organizationId;
    private table = viewChild<TableComponent>('table');

    async openDetail(item?: MicrosoftGraphKeysSettingObject) {
        const header = item
            ? this.prefix + 'dialogEditTitle'
            : this.prefix + 'dialogCreateTitle';

        const dialog = this.dialogService.open(
            MicrosoftGraphKeysSettingDialogComponent,
            {
                header: translate.instant(header),
                data: {
                    item,
                },
            },
        );

        dialog.onClose.subscribe((result) => {
            if (result) {
                this.reloadTable();
            }
        })
    }

    async deleteItem(event: any) {
        this.confirm.confirmDelete(
            () => {
                this.store.api.deleteMicrosoftGraphKeysSetting({ id: event.item.id })
                    .then((ok) => {
                        if (ok?.ok) {
                            successDeleted();
                            this.reloadTable();
                        }
                    });
            },
            event,
        );
    }

    private reloadTable() {
        this.table()?.reload();
    }
}
